import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useAuth, usePermission } from '@saas-kit/lib';
import { BackButton, ErrorBanner, PageHeader, Skeleton } from '@saas-kit/ui';

import { TenantRequiredState } from '../../components/TenantRequiredState';
import { DisplayForm } from './DisplayForm';
import { DisplayLinkCard } from './DisplayLinkCard';
import { useDisplays } from './useDisplays';

/** Add / edit a single display screen on its own page. After a create,
 *  the one-time signed link is revealed here before returning to the list. */
export function DisplayEditPage(): JSX.Element {
  const { t } = useTranslation('qms');
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const selectedTenant = useAuth((s) => s.selectedTenant);
  const canManage = usePermission('qms.displays.manage');
  const displays = useDisplays();

  const toList = (): void => navigate('/qms/displays');

  if (!selectedTenant) {
    return (
      <TenantRequiredState
        title={t('tenant_required.title')}
        body={t('tenant_required.body')}
        cta={t('tenant_required.cta')}
      />
    );
  }
  if (!canManage) {
    return <ErrorBanner>{t('errors.displays_denied')}</ErrorBanner>;
  }

  const editing = id
    ? (displays.displays.find((d) => d.id === id) ?? null)
    : null;

  return (
    <div className="space-y-4">
      <BackButton to="/qms/displays" label={t('displays.back')} />
      <PageHeader
        title={t(
          id ? 'displays.form.edit_title' : 'displays.form.create_title',
        )}
      />

      {displays.revealed ? (
        <DisplayLinkCard
          link={displays.revealed}
          onDismiss={() => {
            displays.dismissRevealed();
            toList();
          }}
        />
      ) : id && displays.isLoading ? (
        <Skeleton className="h-64" />
      ) : id && !editing ? (
        <ErrorBanner>{t('displays.empty')}</ErrorBanner>
      ) : (
        <DisplayForm
          groups={displays.groups}
          counters={displays.counters}
          editing={editing}
          busy={displays.creating || displays.updating}
          onCreate={(input) => displays.create(input)}
          onUpdate={async (displayId, input) => {
            await displays.update(displayId, input);
            toList();
          }}
          onCancelEdit={toList}
        />
      )}
    </div>
  );
}
